import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { NavLink } from "react-router-dom";
import { useMediaQuery, useTheme, Theme, Box } from "@mui/material";
import HeaderLogo from "../../../../assets/images/GL-Logo.svg";
import HamburgerNav from "./HamburgerNav";
import { PATHS } from "../../../../constants/pathConstants";
import Translate from "../../../../translate/Translate";
import { useLogout } from "../../../../hooks/useLogout";
import { useAuth0 } from "@auth0/auth0-react";
import { validateToken } from "../../../../utils/utils";
import { getCookie } from "utils/v3/CookieUtil";
import { COLORS } from "../../Theme/colors";

export interface HeaderItem {
  id: string;
  name: string;
  route: string;
  /** Use for `/` so NavLink does not match every path. */
  exact?: boolean;
  onClick?: () => void;
}

const HEADER_ITEMS: HeaderItem[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    route: PATHS.LANDING_PAGE,
    exact: true
  },
  {
    id: "HelpCenterHeader",
    name: "Support & Resources",
    route: PATHS.HELP_CENTER_ROOT
  },
  {
    id: "account",
    name: "Account",
    route: PATHS.ACCOUNT_MANAGEMENT
  }
];

const HeaderLink: React.FC<{ item: HeaderItem }> = ({ item }) => {
  return (
    <NavLink
      to={item.route}
      id={item.id}
      exact={item.exact}
      onClick={item.onClick}
      style={{ textDecoration: "none", color: COLORS.PRIMARY }}
      activeStyle={{ color: COLORS.ACCENT2, fontWeight: 600 }}
    >
      <Typography
        variant="body_strong_primary"
        component="span"
        sx={{ color: "inherit" }}
      >
        <Translate text={item.name} />
      </Typography>
    </NavLink>
  );
};

const Header: React.FC<{
  showNav?: boolean;
  makeLogoClickable?: boolean;
}> = ({ showNav = true, makeLogoClickable = true }) => {
  const logoutFunction = useLogout();
  const theme = useTheme() as Theme;
  const screenWidthIsMdOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));
  const { loginWithRedirect } = useAuth0();
  const sessionIsValid = validateToken(getCookie("accessToken"));

  const loginOrLogoutLink = (
    <Box
      component="a"
      id="logOut"
      href="#"
      onClick={(e) => {
        e.preventDefault();
        if (sessionIsValid) {
          logoutFunction();
        } else {
          loginWithRedirect();
        }
      }}
      sx={{
        color: COLORS.ERROR,
        textDecoration: "none",
        cursor: "pointer",
        transition: "color 0.3s ease",
        "&:hover": {
          color: COLORS.PRIMARY_DARKER
        }
      }}
    >
      <Typography variant="body_strong_error">
        {sessionIsValid ? "Log Out" : "Log In"}
      </Typography>
    </Box>
  );

  return (
    <AppBar position="static" elevation={0} color="transparent">
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, lg: 4 }
        }}
      >
        {makeLogoClickable ? (
          <NavLink to={PATHS.LANDING_PAGE}>
            <img
              src={HeaderLogo}
              alt="Logo"
              style={{
                height: "24px",
                width: "auto"
              }}
            />
          </NavLink>
        ) : (
          <img
            src={HeaderLogo}
            alt="Logo"
            style={{
              height: "24px",
              width: "auto"
            }}
          />
        )}

        {screenWidthIsMdOrSmaller
          ? showNav && <HamburgerNav menuItems={HEADER_ITEMS} />
          : showNav && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "32px"
                }}
              >
                {HEADER_ITEMS.map((item) => (
                  <HeaderLink key={item.id} item={item} />
                ))}
                {loginOrLogoutLink}
              </Box>
            )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
